import { authFetch } from 'auth/utils.ts';
import { envVariables } from 'common/utils/envVariables.ts';
import { saveAs } from 'file-saver';
import {
  downloadFile,
  getFileNameFromContentDispositionHeader,
} from 'common/utils/fileDownload.ts';
import {
  PROVISIONING_STATUS,
  PROVISIONING_STATUS_TO_STATUS_LABEL_MAP,
  ProvisioningStatus,
} from '@/activityLog/bulkProvisioning/constants/provisioningStatus.ts';
import { parseISO } from 'date-fns';
import { ProductsManagementFilters } from 'atoms/filters/products-management-filters.ts';

export const PRODUCTS_MANAGEMENT_TEMPLATES = {
  CUSTOMER: 'ProductManagement',
  INTERNAL: 'ProductManagementInternal',
} as const;

export const downloadProductsManagementTemplate = async (filename: string) => {
  const response = await authFetch(
    `${envVariables.VITE_BULK_PRODUCTS_MANAGEMENT_MODULE_REQUESTS_API}/templates/${filename}`,
  );

  if (!response.ok) {
    throw new Error(response.statusText);
  }

  const defaultFilename = getFileNameFromContentDispositionHeader(
    response.headers.get('Content-Disposition'),
  );

  const blob = await response.blob();

  saveAs(blob, defaultFilename ?? `${filename}.xlsx`);
};

type ProductManagementErrorDto = {
  productIdentifier: string;
  errorMessage: string;
};

export type ProductManagementErrorsResponse = {
  items: ProductManagementErrorDto[];
  hasMoreErrors: boolean;
};

export const fetchProductManagementErrors = async (
  requestId: string,
  signal?: AbortSignal,
): Promise<ProductManagementErrorsResponse> => {
  const response = await authFetch(
    `${envVariables.VITE_BULK_PRODUCTS_MANAGEMENT_MODULE_REQUESTS_API}/${requestId}/errors`,
    { signal },
  );

  if (!response.ok) {
    throw new Error(response.status.toString());
  }

  return response.json();
};

export const downloadProductManagementErrorSummary = async (id: string) => {
  const response = await authFetch(
    `${envVariables.VITE_BULK_PRODUCTS_MANAGEMENT_MODULE_REQUESTS_API}/${id}/error-summary`,
  );

  if (!response.ok) {
    throw new Error(response.status.toString());
  }

  const filename = getFileNameFromContentDispositionHeader(
    response.headers.get('Content-Disposition'),
  );

  const blob = await response.blob();

  saveAs(blob, filename);
};

type ProductsManagementFetchProps = ProductsManagementFilters & {
  page: number;
  pageSize: number;
};

type ProductsManagementItemDTO = {
  id: string;
  fileName: string;
  status: ProvisioningStatus;
  numberOfProducts: number;
  succeed: number;
  failed: number;
  uploadTime: string;
  processingFinishTime: string;
  userName: string;
  errorSummaryFileName: string;
};

type ProductsManagementRequestsDto = {
  items: ProductsManagementItemDTO[];
  totalPages: number;
  totalCount: number;
};

export const fetchProductManagement = async (
  { page, pageSize, status, uploadDateFrom, uploadDateTo, fileName }: ProductsManagementFetchProps,
  signal?: AbortSignal,
) => {
  const urlQuery = new URLSearchParams({
    PageNumber: (page + 1).toString(),
    PageSize: pageSize.toString(),
  });

  urlQuery.append('FileName', fileName);

  if (status !== '') {
    urlQuery.append('Statuses', [status].toString());
  }

  if (uploadDateFrom) {
    urlQuery.append('UploadDateFrom', uploadDateFrom.toISOString());
  }

  if (uploadDateTo) {
    urlQuery.append('UploadDateTo', uploadDateTo.toISOString());
  }

  const productsManagementResponse = await authFetch(
    `${envVariables.VITE_BULK_PRODUCTS_MANAGEMENT_MODULE_REQUESTS_API}?` + urlQuery.toString(),
    { signal },
  );

  if (!productsManagementResponse.ok) {
    throw new Error(productsManagementResponse.status.toString());
  }

  const productsManagementData: ProductsManagementRequestsDto =
    await productsManagementResponse.json();

  return {
    items: productsManagementData.items.map(
      ({
        id,
        status,
        fileName,
        uploadTime,
        processingFinishTime,
        failed,
        succeed,
        numberOfProducts,
        userName,
        errorSummaryFileName,
      }) => ({
        id,
        status: PROVISIONING_STATUS_TO_STATUS_LABEL_MAP[status],
        numberOfCards: numberOfProducts,
        numberOfSuccessCards: succeed,
        numberOfFailedCards: failed,
        fileName,
        uploadAt: parseISO(uploadTime),
        uploadFinishedAt:
          status === PROVISIONING_STATUS.PROCESSING_SUCCEED ? parseISO(processingFinishTime) : null,
        uploadBy: userName,
        errorSummaryFileName: errorSummaryFileName,
      }),
    ),
    numberOfPages: productsManagementData.totalPages,
    totalNumberOfItems: productsManagementData.totalCount,
  };
};

export const downloadProductsManagementInputFile = async (id: string) =>
  downloadFile(`${envVariables.VITE_BULK_PRODUCTS_MANAGEMENT_MODULE_REQUESTS_API}/${id}/file`);
